/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-tagless-components */
import Component from '@ember/component';
import { computed } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { isEmpty } from '@ember/utils';

// D3 imports
import { select } from 'd3-selection';
import { scaleLinear, scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { area as d3Area, line as d3Line, stack as d3Stack } from 'd3-shape';
import tip from '@linc-technologies/linced-common/utils/d3-tip';
import {
  attributeBindings,
  classNames,
  tagName,
} from '@ember-decorators/component';

@tagName('svg')
@classNames('chart', 'chart-area')
@attributeBindings('width', 'height')
export default class ChartArea extends Component {
  width = 480;
  height = 320;
  margin = Object.freeze({ top: 20, right: 20, bottom: 30, left: 20 });
  colors = schemeCategory10;
  data = [];

  @computed('data.[]')
  get processedData() {
    let rawData = this.data;
    let data = [];
    if (isEmpty(rawData)) {
      return rawData;
    }
    let keys = Object.keys(rawData[0].data);
    let maxValue = max(rawData, (d) => max(Object.values(d.data)));

    rawData.forEach((tick) => {
      let prev = 0;
      let processed = {
        label: tick.label,
        value: tick.value,
      };

      Object.keys(tick.data).forEach((key) => {
        processed[key] = tick.data[key] - prev;
        prev = tick.data[key];
      });

      data.push(processed);
    });

    data.columns = keys;
    data.__maxValue = maxValue;

    return data;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `draw` method on Ember's "render" queue, which will
    // happen after the component has been placed in the DOM, and subsequently
    // each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let plot = select(this.element);
    let data = this.processedData;
    if (isEmpty(data)) {
      // No point trying to chart an empty data set.
      return;
    }
    let keys = data.columns;
    let margin = this.margin;
    let width = this.width - margin.left - margin.right;
    let height = this.height - margin.top - margin.bottom;

    let xStep = Math.floor(width / (data.length - 1));
    let xRange = data.map((_, idx) => idx * xStep);

    let x = scaleOrdinal().range(xRange),
      y = scaleLinear().range([height, 0]),
      color = scaleOrdinal(this.colors);

    let stack = d3Stack();

    let area = d3Area()
      .x((d) => x(d.data.value))
      .y0(y(0))
      .y1((d) => y(d[1]));

    let areaLine = d3Line()
      .x((d) => x(d.data.value))
      .y((d) => y(d[1]));

    // Remove previous chart (if there is one), then append the new root element.
    plot.selectAll('*').remove();
    let g = plot
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    x.domain(data.map((d) => d.value));
    y.domain([0, data.__maxValue]);
    color.domain(keys);
    stack.keys(keys);

    // Tooltip
    let tooltip = tip()
      .attr('class', 'd3-tip')
      .html((d) => d.key);
    g.call(tooltip);

    let layer = g
      .selectAll('.layer')
      .data(stack(data))
      .enter()
      .append('g')
      .attr('class', 'layer');

    layer
      .append('path')
      .attr('class', 'area')
      .attr('fill-opacity', '0.3')
      .style('fill', function (d) {
        return color(d.key);
      })
      .attr('d', area)
      .on('mouseover', tooltip.show)
      .on('mouseout', tooltip.hide);

    let layerLine = g
      .selectAll('.layer-line')
      .data(stack(data))
      .enter()
      .append('g')
      .attr('class', 'layer-line')
      .append('g');

    layerLine
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', function (d) {
        return color(d.key);
      })
      .attr('stroke-width', 1)
      .attr('d', areaLine);

    g.append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', 'translate(0,' + height + ')')
      .call(axisBottom(x).tickFormat((t) => this.tickLabel(t)));

    g.append('g')
      .attr('class', 'axis axis--y')
      .call(axisLeft(y).ticks(Math.min(5, data.__maxValue)));
  }

  tickLabel(val) {
    return this.data.find((point) => point.value === val).label;
  }
}
